import React, { useState } from 'react';
import { Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { sendEmail } from '../lib/emailjs';

const subjects = [
  { value: 'cours', label: 'Question sur les cours particuliers' },
  { value: 'flashcards', label: 'Question sur les flashcards' },
  { value: 'sujets-bac', label: 'Question sur les sujets de bac' },
  { value: 'ressources', label: 'Accès aux ressources gratuites' },
  { value: 'technique', label: 'Problème technique' },
  { value: 'paiement', label: 'Question sur un paiement' },
  { value: 'partenariat', label: 'Proposition de partenariat' },
  { value: 'autre', label: 'Autre demande' }
];

const schema = z.object({
  email: z.string()
    .email('Email invalide')
    .min(1, 'L\'email est requis'),
  subject: z.string()
    .min(1, 'Veuillez sélectionner un sujet'),
  message: z.string()
    .min(10, 'Le message doit contenir au moins 10 caractères')
    .max(1000, 'Le message ne doit pas dépasser 1000 caractères')
});

type FormData = z.infer<typeof schema>;

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageLength, setMessageLength] = useState(0);
  
  const { 
    register, 
    handleSubmit, 
    reset,
    watch,
    formState: { errors } 
  } = useForm<FormData>({
    resolver: zodResolver(schema)
  });

  React.useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'message') {
        setMessageLength(value.message?.length || 0);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      await sendEmail(data);
      toast.success('Message envoyé avec succès !');
      reset();
      setMessageLength(0);
    } catch (error) {
      toast.error('Une erreur est survenue lors de l\'envoi du message.');
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 bg-gradient-to-b from-white to-[#9a6ebd]/10">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#9a6ebd]/10 rounded-full mb-4">
            <Mail className="w-8 h-8 text-[#9a6ebd]" />
          </div>
          <h2 className="text-3xl font-bold text-[#323c5f] mb-2">
            Une question ?
          </h2>
          <p className="text-xl text-gray-600">
            Nous sommes là pour vous aider !
          </p>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-[#ceace8]">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#323c5f] mb-2">
                  Votre email
                </label>
                <input
                  type="email"
                  id="email"
                  placeholder="exemple@email.com"
                  {...register('email')}
                  className={`w-full px-4 py-3 rounded-xl shadow-sm transition-colors
                    ${errors.email 
                      ? 'border-2 border-red-300 focus:border-red-300 focus:ring focus:ring-red-200'
                      : 'border-2 border-[#9a6ebd]/20 focus:border-[#9a6ebd] focus:ring focus:ring-[#9a6ebd]/50'
                    }`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <span className="block w-1 h-1 rounded-full bg-red-600 mr-2"></span>
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-[#323c5f] mb-2">
                  Sujet de votre message
                </label>
                <select
                  id="subject"
                  {...register('subject')}
                  className={`w-full px-4 py-3 rounded-xl shadow-sm transition-colors appearance-none bg-white
                    ${errors.subject 
                      ? 'border-2 border-red-300 focus:border-red-300 focus:ring focus:ring-red-200'
                      : 'border-2 border-[#9a6ebd]/20 focus:border-[#9a6ebd] focus:ring focus:ring-[#9a6ebd]/50'
                    }`}
                >
                  <option value="">Sélectionnez un sujet</option>
                  {subjects.map((subject) => (
                    <option key={subject.value} value={subject.value}>
                      {subject.label}
                    </option>
                  ))}
                </select>
                {errors.subject && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <span className="block w-1 h-1 rounded-full bg-red-600 mr-2"></span>
                    {errors.subject.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-[#323c5f] mb-2">
                  Votre message
                </label>
                <div className="relative">
                  <textarea
                    id="message"
                    rows={6}
                    placeholder="Décrivez votre question en détail..."
                    {...register('message')}
                    className={`w-full px-4 py-3 rounded-xl shadow-sm transition-colors
                      ${errors.message 
                        ? 'border-2 border-red-300 focus:border-red-300 focus:ring focus:ring-red-200'
                        : 'border-2 border-[#9a6ebd]/20 focus:border-[#9a6ebd] focus:ring focus:ring-[#9a6ebd]/50'
                      }`}
                  ></textarea>
                  <div className="absolute bottom-3 right-3 text-sm text-gray-500 bg-white px-2 rounded-md">
                    {messageLength}/1000
                  </div>
                </div>
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <span className="block w-1 h-1 rounded-full bg-red-600 mr-2"></span>
                    {errors.message.message}
                  </p>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-[#9a6ebd] text-white px-6 py-4 rounded-xl hover:bg-[#8a5dad] transition-colors font-medium
                ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Envoi en cours...
                </div>
              ) : (
                'Envoyer mon message'
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;